import * as React from 'react';
import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { StatsigProvider } from '@coursera-web/statsig';

import StoreProviderWrapper from 'bundles/ai-coach-platform/components/patterns/chat/store/StoreProviderWrapper';
import DiscoveryCollectionsQuery from 'bundles/browse-common/legacy/queries/DiscoveryCollectionsQuery.graphql';
import type {
  DiscoveryCollectionsQuery as DiscoveryCollectionsQueryData,
  DiscoveryCollectionsQueryVariables,
} from 'bundles/browse-common/legacy/queries/__generated__/DiscoveryCollectionsQuery';
import SearchBarContentLegacy from 'bundles/search-common/components/searchbar/SearchBarContentLegacy';
import SearchBarContentWrapper from 'bundles/search-common/components/searchbar/SearchBarContentWrapper';
import type { SearchBarLocation } from 'bundles/search-common/constants';
import useWarmSession from 'bundles/search-common/hooks/useWarmSession';
import { useAiSearchExperimentData } from 'bundles/search-common/providers/AiSearchExperimentProvider';
import { getItemsListFromLocalStorageByKeyAndTrimOutdated } from 'bundles/search-common/utils/SearchUtils';
import {
  getShowNewAutocomplete,
  impressIntoNewAutocompleteExperiment,
} from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';

type Props = {
  hideSearch?: boolean;
  searchIsOpen?: boolean;
  pageLocation?: SearchBarLocation;
  shouldFocusSearch?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  hideMobileSearchPage?: () => void;
  searchInputIsFocused?: boolean;
  enableOneStepSearch?: boolean;
  shouldShowExposedSearchAndReg?: boolean;
  smallSearchBar?: boolean;
  className?: string;
};

const WarmSessionOnMount = () => {
  const warmSession = useWarmSession();
  useEffect(() => {
    warmSession();
  }, [warmSession]);
  return null;
};

const SearchBar = ({
  pageLocation,
  searchIsOpen,
  shouldFocusSearch,
  onBlur,
  onFocus,
  hideMobileSearchPage,
  enableOneStepSearch,
  shouldShowExposedSearchAndReg,
  searchInputIsFocused,
  className,
  smallSearchBar,
  hideSearch,
}: Props) => {
  const isSearchPage = useIsConsumerSerp();
  const { isVariant2 } = useAiSearchExperimentData();

  const { data } = useQuery<DiscoveryCollectionsQueryData, DiscoveryCollectionsQueryVariables>(
    DiscoveryCollectionsQuery,
    {
      skip: getShowNewAutocomplete()
        ? getItemsListFromLocalStorageByKeyAndTrimOutdated('recently-viewed').length >= 4
        : getItemsListFromLocalStorageByKeyAndTrimOutdated('recently-viewed').length !== 0,
      variables: {
        contextType: 'PAGE',
        contextId: getShowNewAutocomplete() ? 'autocomplete-repeat-search-state' : 'search-zero-state',
      },
      context: { clientName: 'gatewayGql' },
      ssr: false,
    }
  );
  const collectionRecommendations = data?.DiscoveryCollections?.queryCollections?.[0];

  if (impressIntoNewAutocompleteExperiment()) {
    if (hideSearch) return null;
    return (
      <StatsigProvider>
        <StoreProviderWrapper>
          <SearchBarContentWrapper
            collectionRecommendations={collectionRecommendations}
            searchIsOpen={searchIsOpen}
            hideMobileSearchPage={hideMobileSearchPage}
            searchBarConfig={{ enableAiSearchSuggestions: isVariant2 }}
          />
        </StoreProviderWrapper>
      </StatsigProvider>
    );
  }

  return (
    <StatsigProvider>
      <WarmSessionOnMount />
      <SearchBarContentLegacy
        collectionRecommendations={collectionRecommendations}
        onBlur={onBlur}
        onFocus={() => {
          onFocus?.();
        }}
        searchIsOpen={searchIsOpen}
        hideMobileSearchPage={hideMobileSearchPage}
        enableOneStepSearch={enableOneStepSearch}
        shouldShowExposedSearchAndReg={shouldShowExposedSearchAndReg}
        searchInputIsFocused={searchInputIsFocused}
        isSearchPage={isSearchPage}
        pageLocation={pageLocation}
        shouldFocusSearch={shouldFocusSearch}
        className={className}
        smallSearchBar={smallSearchBar}
        hideSearch={hideSearch}
      />
    </StatsigProvider>
  );
};

export default SearchBar;
